import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../contexts/ApiContext';
import { CircularProgress, Box } from '@mui/material';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: 'owner' | 'admin' | 'user' | 'guest';
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [checkingPageAccess, setCheckingPageAccess] = useState(false);
  const [hasPageAccess, setHasPageAccess] = useState<boolean | null>(null);

  // Check page permissions for non-admin/owner users
  useEffect(() => {
    const checkPagePermission = async () => {
      // Admin and owner have access to all pages
      if (!user || user.role === 'admin' || user.role === 'owner') {
        setHasPageAccess(true);
        return;
      }

      // Skip permission check for auth pages and root path (which redirects)
      if (location.pathname === '/auth' || location.pathname === '/auth-test' || location.pathname === '/landing' || location.pathname === '/') {
        setHasPageAccess(true);
        return;
      }

      try {
        setCheckingPageAccess(true);
        const response = await api.get(`/api/admin/page-permissions/check${location.pathname}`);
        setHasPageAccess(response.data.can_access);
      } catch (err: any) {
        // If API fails, default to DENYING access (fail closed for security)
        console.error('Error checking page permission:', err);
        // Only allow access if it's a network error, not a 404 (which means no permission)
        if (err.response?.status === 404) {
          // 404 means no permission record found - deny access
          setHasPageAccess(false);
        } else {
          // For other errors (network, etc.), be more lenient but log it
          console.warn('Permission check failed, denying access by default:', err);
          setHasPageAccess(false);
        }
      } finally {
        setCheckingPageAccess(false);
      }
    };

    if (!loading && isAuthenticated && user) {
      checkPagePermission();
    }
  }, [location.pathname, user, loading, isAuthenticated]);

  if (loading || checkingPageAccess) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace state={{ from: location }} />;
  }

  if (requiredRole && user?.role !== requiredRole && user?.role !== 'admin' && user?.role !== 'owner') {
    return <Navigate to="/" replace />;
  }

  // For non-admin/owner users, wait for permission check to complete
  // Don't render children until we know if they have access
  if (user && user.role !== 'admin' && user.role !== 'owner') {
    // Skip check for auth pages and root (already handled above)
    const skipCheckPages = ['/auth', '/auth-test', '/landing', '/'];
    if (!skipCheckPages.includes(location.pathname)) {
      // If permission check hasn't completed yet, show loading
      if (hasPageAccess === null) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
            <CircularProgress />
          </Box>
        );
      }
      // If permission check completed and access is denied, redirect
      if (hasPageAccess === false) {
        return <Navigate to="/" replace />;
      }
    }
  }

  return <>{children}</>;
};